import React, { useState, useEffect, useContext } from 'react';
import { API_URL } from '../config';
import { AuthContext } from '../context/AuthContext';

const PREDEFINED_FEATURES = [
  "High-frequency scalping",
  "Tight spread optimization",
  "Capital preservation logic",
  "Fast execution required",
  "Fully automated trading",
  "Grid + Martingale hybrid",
  "News filter included",
  "Prop firm ready",
  "Trailing stop loss",
  "Multi-currency support"
];

const PREDEFINED_EXT_FEATURES = [
  "Works 24/7 without emotions",
  "Executes trades instantly",
  "Never hesitates",
  "Never overtrades",
  "Follows strict algorithmic rules",
  "Continuously protects active positions",
  "Advanced trailing stop logic",
  "Hidden stop loss & take profit"
];

const AdminDashboard = () => {
  const { token } = useContext(AuthContext);
  
  // Navigation State
  const [activeTab, setActiveTab] = useState('products'); // products, contact, custom_ea, request_ea, newsletter

  // Data States
  const [products, setProducts] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [customEAs, setCustomEAs] = useState([]);
  const [eaRequests, setEaRequests] = useState([]);
  const [newsletterSubs, setNewsletterSubs] = useState([]);

  // Promos
  const [promos, setPromos] = useState([]);
  const [userPromos, setUserPromos] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: '', discount_percent: 10, affiliate_id: '' });

  // Editor State (for products)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [queuedFiles, setQueuedFiles] = useState({ image: null, backtestImage: null, eaFile: null });
  const [isGeneratingSeo, setIsGeneratingSeo] = useState(false);

  useEffect(() => {
    if (activeTab === 'products') fetchProducts();
    if (activeTab === 'contact') fetchContactMessages();
    if (activeTab === 'custom_ea') fetchCustomEAs();
    if (activeTab === 'request_ea') fetchEaRequests();
    if (activeTab === 'newsletter') fetchNewsletterSubs();
    if (activeTab === 'promos') fetchPromos();
  }, [activeTab]);

  const fetchProducts = () => {
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error(err));
  };

  const fetchContactMessages = () => {
    fetch(`${API_URL}/api/admin/forms/contact`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setContactMessages(data))
      .catch(err => console.error(err));
  };

  const fetchCustomEAs = () => {
    fetch(`${API_URL}/api/admin/forms/custom-ea`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setCustomEAs(data))
      .catch(err => console.error(err));
  };

  const fetchEaRequests = () => {
    fetch(`${API_URL}/api/admin/forms/request-ea`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setEaRequests(data))
      .catch(err => console.error(err));
  };

  const fetchNewsletterSubs = () => {
    fetch(`${API_URL}/api/admin/forms/newsletter`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setNewsletterSubs(data))
      .catch(err => console.error(err));
  };

  const fetchPromos = () => {
    fetch(`${API_URL}/api/admin/promos`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => { setPromos(data.promos || []); setUserPromos(data.userPromos || []); })
      .catch(err => console.error(err));
  };
  
  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if(!newPromo.code) return alert('Code required');
    try {
      const res = await fetch(`${API_URL}/api/admin/promos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newPromo)
      });
      if(res.ok) {
        setNewPromo({ code: '', discount_percent: 10, affiliate_id: '' });
        fetchPromos();
      } else {
        alert('Error creating promo code');
      }
    } catch (err) { console.error(err); }
  };
  
  const handleDeletePromo = async (id) => {
    if(!window.confirm('Delete this promo code?')) return;
    try {
      await fetch(`${API_URL}/api/admin/promos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchPromos();
    } catch (err) { console.error(err); }
  };

  // Product Editor Functions
  const handleSelect = (p) => {
    setSelectedProduct(p);
    const whatsIncludedStr = p.whatsIncluded 
      ? p.whatsIncluded.map(item => `${item.title} | ${item.subtitle} | ${item.icon}`).join('\n')
      : '';
    setFormData({
      ...p,
      features: p.features ? p.features.join('\n') : '',
      extendedFeatures: p.extendedFeatures ? p.extendedFeatures.join('\n') : '',
      whatsIncluded: whatsIncludedStr
    });
    setQueuedFiles({ image: null, backtestImage: null, eaFile: null });
  };

  const handleNew = () => {
    setSelectedProduct({ id: 'new' });
    setFormData({ 
      id: '', name: '', price: '', description: '', features: '', extendedFeatures: '', whatsIncluded: '',
      category: 'Trend Following', platform: 'MetaTrader 5 (MT5)', badge: 'None', backtestImage: ''
    });
    setQueuedFiles({ image: null, backtestImage: null, eaFile: null });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && selectedProduct.id === 'new') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
      setFormData({ ...formData, [name]: value, id: slug });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleFeature = (field, feature) => {
    const currentVal = formData[field] || '';
    let lines = currentVal.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.includes(feature)) {
      lines = lines.filter(l => l !== feature);
    } else {
      lines.push(feature);
    }
    setFormData({ ...formData, [field]: lines.join('\n') });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const parsedWhatsIncluded = formData.whatsIncluded 
      ? formData.whatsIncluded.split('\n').filter(f => f.trim() !== '').map(line => {
          const parts = line.split('|').map(s => s.trim());
          return { title: parts[0] || '', subtitle: parts[1] || '', icon: parts[2] || 'terminal' };
        })
      : [];

    const payload = {
      ...formData,
      features: formData.features.split('\n').filter(f => f.trim() !== ''),
      extendedFeatures: formData.extendedFeatures.split('\n').filter(f => f.trim() !== ''),
      whatsIncluded: parsedWhatsIncluded
    };
    const isNew = selectedProduct && selectedProduct.id === 'new';
    const url = isNew ? `${API_URL}/api/products` : `${API_URL}/api/products/${selectedProduct.id}`;
    const method = isNew ? 'POST' : 'PUT';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const productId = payload.id;
        let filesUploaded = false;

        if (queuedFiles.image) {
          await uploadQueuedImage(queuedFiles.image, productId, 'image');
          filesUploaded = true;
        }
        if (queuedFiles.backtestImage) {
          await uploadQueuedImage(queuedFiles.backtestImage, productId, 'backtestImage');
          filesUploaded = true;
        }
        if (queuedFiles.eaFile) {
          await uploadQueuedEaFile(queuedFiles.eaFile, productId);
          filesUploaded = true;
        }

        if (filesUploaded) {
          alert('Product saved and files uploaded successfully!');
        } else {
          alert('Saved successfully!');
        }

        fetchProducts();
        setSelectedProduct(null);
        setQueuedFiles({ image: null, backtestImage: null, eaFile: null });
      } else {
        alert('Error saving product.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error.');
    }
  };

  const uploadQueuedImage = async (file, productId, field) => {
    const uploadData = new FormData();
    uploadData.append('imageFile', file);
    uploadData.append('field', field);
    try {
      await fetch(`${API_URL}/api/admin/upload-image/${productId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadData
      });
    } catch (err) {
      console.error('Image upload error:', err);
    }
  };

  const uploadQueuedEaFile = async (file, productId) => {
    const uploadData = new FormData();
    uploadData.append('eaFile', file);
    try {
      await fetch(`${API_URL}/api/admin/upload-ea/${productId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadData
      });
    } catch (err) {
      console.error('EA upload error:', err);
    }
  };

  const handleQueueFile = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setQueuedFiles(prev => ({ ...prev, [type]: file }));
    }
  };

  const handleDelete = async () => {
    if (selectedProduct.id === 'new') return;
    if (!window.confirm(`Are you sure you want to delete ${selectedProduct.name}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/products/${selectedProduct.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Deleted successfully!');
        fetchProducts();
        setSelectedProduct(null);
      } else {
        alert('Error deleting product.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error.');
    }
  };

  const handleGenerateSeo = async () => {
    if (!formData.name) {
      alert("Please enter a Name for the EA first.");
      return;
    }
    
    setIsGeneratingSeo(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/generate-seo-description`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          name: formData.name,
          features: formData.features,
          winRate: formData.winRate,
          maxDrawdown: formData.maxDrawdown,
          category: formData.category
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          description: data.description,
          longDescription: data.longDescription
        }));
      } else {
        alert(data.error || 'Failed to generate SEO description');
      }
    } catch (err) {
      console.error('SEO Generation error:', err);
      alert('Network error while generating SEO description.');
    } finally {
      setIsGeneratingSeo(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto pb-24">
      <h1 className="text-headline-md font-bold text-primary mb-8">Admin Control Center</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        
        {/* Sidebar */}
        <div className="glass-panel p-4 rounded-xl h-fit flex flex-col gap-2">
          <button onClick={() => setActiveTab('products')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'products' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">inventory_2</span>
            EA Products
          </button>
          <button onClick={() => setActiveTab('custom_ea')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'custom_ea' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">engineering</span>
            Custom EA Requests
          </button>
          <button onClick={() => setActiveTab('request_ea')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'request_ea' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">shopping_cart_checkout</span>
            EA sourcing
          </button>
          <button onClick={() => setActiveTab('contact')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'contact' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">mail</span>
            Contact Messages
          </button>
          <button onClick={() => setActiveTab('newsletter')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'newsletter' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">campaign</span>
            Newsletter Subs
          </button>
          <button onClick={() => setActiveTab('promos')} className={`text-left px-4 py-3 rounded transition-colors font-bold ${activeTab === 'promos' ? 'bg-primary/20 text-primary border border-primary/30' : 'hover:bg-white/5 text-silver-text'}`}>
            <span className="material-symbols-outlined align-bottom mr-2 text-[18px]">loyalty</span>
            Promo Codes
          </button>
        </div>

        {/* Main Content Area */}
        <div className="md:col-span-3 glass-panel p-6 rounded-xl overflow-x-auto">
          
          {/* TAB: PRODUCTS */}
          {activeTab === 'products' && (
            <div>
              {!selectedProduct ? (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-headline-sm font-bold">Manage Products</h2>
                    <button onClick={handleNew} className="bg-primary text-on-primary px-4 py-2 rounded font-bold text-sm">
                      + Add New EA
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {products.map(p => (
                      <button 
                        key={p.id} 
                        onClick={() => handleSelect(p)}
                        className="text-left px-4 py-3 rounded bg-surface-variant hover:bg-white/10 transition-colors flex justify-between items-center"
                      >
                        <span className="font-bold">{p.name}</span>
                        <span className="text-[12px] text-on-surface-variant bg-black/20 px-2 py-1 rounded">{p.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSave} className="flex flex-col gap-4">
                  <div className="flex items-center gap-4 mb-4">
                    <button 
                      type="button" 
                      onClick={() => setSelectedProduct(null)}
                      className="bg-surface-variant text-white px-3 py-1.5 rounded flex items-center gap-2 hover:bg-white/10 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                      Back
                    </button>
                    <h2 className="text-headline-sm font-bold m-0">
                      {selectedProduct.id === 'new' ? 'Add New EA' : `Edit ${selectedProduct.name}`}
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">ID (Slug - Auto Generated)</label>
                      <input name="id" value={formData.id || ''} readOnly disabled className="w-full bg-black/20 p-2 rounded text-white/50 cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Name</label>
                      <input name="name" value={formData.name || ''} onChange={handleChange} required className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-label-sm text-on-surface-variant mb-1">Tagline</label>
                    <input name="tagline" value={formData.tagline || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Category</label>
                      <select name="category" value={formData.category || 'Trend Following'} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white outline-none">
                        <option value="Scalper">Scalper</option>
                        <option value="Grid / Martingale">Grid / Martingale</option>
                        <option value="Trend Following">Trend Following</option>
                        <option value="Arbitrage">Arbitrage</option>
                        <option value="News Trading">News Trading</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Platform</label>
                      <select name="platform" value={formData.platform || 'MetaTrader 5 (MT5)'} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white outline-none">
                        <option value="MetaTrader 4 (MT4)">MetaTrader 4 (MT4)</option>
                        <option value="MetaTrader 5 (MT5)">MetaTrader 5 (MT5)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-label-sm text-on-surface-variant mb-1">Sort Badge (Highlight)</label>
                    <select name="badge" value={formData.badge || 'None'} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white outline-none">
                      <option value="None">None</option>
                      <option value="Top Rated">Top Rated</option>
                      <option value="Lowest Drawdown">Lowest Drawdown</option>
                      <option value="Highest ROI">Highest ROI</option>
                      <option value="Newest Arrivals">Newest Arrivals</option>
                      <option value="Price: Low to High">Price: Low to High</option>
                    </select>
                  </div>

                  <div className="mt-4 p-4 border border-secondary/30 bg-secondary/5 rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-primary opacity-50"></div>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="font-bold text-secondary text-[14px] flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                          AI SEO Generator
                        </h3>
                        <p className="text-[12px] text-on-surface-variant">Auto-write persuasive descriptions using Gemini AI.</p>
                      </div>
                      <button 
                        type="button" 
                        onClick={handleGenerateSeo}
                        disabled={isGeneratingSeo}
                        className="bg-secondary text-on-secondary px-4 py-2 rounded font-bold text-[13px] hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isGeneratingSeo ? (
                          <>
                            <span className="material-symbols-outlined animate-spin text-[16px]">sync</span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined text-[16px]">draw</span>
                            Generate Now
                          </>
                        )}
                      </button>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div>
                        <label className="block text-label-sm text-on-surface-variant mb-1">Short Description</label>
                        <textarea name="description" value={formData.description || ''} onChange={handleChange} rows="3" className="w-full bg-black/30 p-2 rounded text-white border border-white/5 focus:border-secondary/50 outline-none transition-colors" />
                      </div>

                      <div>
                        <label className="block text-label-sm text-on-surface-variant mb-1">Long Description (Strategy)</label>
                        <textarea name="longDescription" value={formData.longDescription || ''} onChange={handleChange} rows="6" className="w-full bg-black/30 p-2 rounded text-white border border-white/5 focus:border-secondary/50 outline-none transition-colors" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Current Price</label>
                      <input name="newPrice" value={formData.newPrice || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Old Price</label>
                      <input name="oldPrice" value={formData.oldPrice || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Price (Legacy)</label>
                      <input name="price" value={formData.price || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Win Rate</label>
                      <input name="winRate" value={formData.winRate || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Max Drawdown</label>
                      <input name="maxDrawdown" value={formData.maxDrawdown || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Profit Factor</label>
                      <input name="profitFactor" value={formData.profitFactor || ''} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <h3 className="font-bold text-white mt-6 mb-2 border-b border-white/10 pb-2">Technical Specifications</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Account Type</label>
                      <input name="accountType" value={formData.accountType || ''} onChange={handleChange} placeholder="ECN / Low Spread" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Currency Pairs</label>
                      <input name="currencyPairs" value={formData.currencyPairs || ''} onChange={handleChange} placeholder="Any (Prop: EURUSD)" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Min. Deposit</label>
                      <input name="minDeposit" value={formData.minDeposit || ''} onChange={handleChange} placeholder="$100" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Leverage</label>
                      <input name="leverage" value={formData.leverage || ''} onChange={handleChange} placeholder="1:30 to 1:500" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Timeframe</label>
                      <input name="timeframe" value={formData.timeframe || ''} onChange={handleChange} placeholder="M1 / M5 / M15" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <h3 className="font-bold text-white mt-6 mb-2 border-b border-white/10 pb-2">Reviews Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Review Style</label>
                      <select name="reviewStyle" value={formData.reviewStyle || 'excellent'} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white outline-none">
                        <option value="excellent">Excellent (Mostly 5 Stars)</option>
                        <option value="good">Good (Mostly 4 & 5 Stars)</option>
                        <option value="mixed">Mixed (All Ratings)</option>
                        <option value="worst">Worst (Mostly 1-3 Stars)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Number of Reviews</label>
                      <input type="number" name="reviewCount" value={formData.reviewCount || 150} onChange={handleChange} className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-label-sm text-on-surface-variant mb-1">Image URL (Main)</label>
                    <div className="flex gap-2">
                      <input name="image" value={formData.image || ''} onChange={handleChange} className="flex-1 bg-surface-variant p-2 rounded text-white" placeholder="/uploads/..." />
                      <label className="bg-primary/20 text-primary px-4 py-2 rounded cursor-pointer hover:bg-primary/30 flex items-center justify-center font-bold text-[12px] truncate max-w-[150px]" title={queuedFiles.image ? queuedFiles.image.name : 'Upload File'}>
                        {queuedFiles.image ? queuedFiles.image.name : 'Upload File'}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleQueueFile(e, 'image')} />
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-label-sm text-on-surface-variant mb-1">Backtest Image URL (Optional)</label>
                    <div className="flex gap-2">
                      <input name="backtestImage" value={formData.backtestImage || ''} onChange={handleChange} className="flex-1 bg-surface-variant p-2 rounded text-white" placeholder="/uploads/..." />
                      <label className="bg-primary/20 text-primary px-4 py-2 rounded cursor-pointer hover:bg-primary/30 flex items-center justify-center font-bold text-[12px] truncate max-w-[150px]" title={queuedFiles.backtestImage ? queuedFiles.backtestImage.name : 'Upload File'}>
                        {queuedFiles.backtestImage ? queuedFiles.backtestImage.name : 'Upload File'}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleQueueFile(e, 'backtestImage')} />
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Features (One per line)</label>
                      <div className="mb-2 flex flex-wrap gap-2">
                        {PREDEFINED_FEATURES.map(f => {
                          const isActive = (formData.features || '').includes(f);
                          return (
                            <span 
                              key={f}
                              onClick={() => toggleFeature('features', f)}
                              className={`cursor-pointer px-2 py-1 rounded text-[11px] border transition-colors ${isActive ? 'bg-primary/20 text-primary border-primary/50' : 'bg-surface-variant text-silver-text border-white/10 hover:bg-white/10'}`}
                            >
                              {f}
                            </span>
                          );
                        })}
                      </div>
                      <textarea name="features" value={formData.features || ''} onChange={handleChange} rows="5" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-label-sm text-on-surface-variant mb-1">Extended Features (One per line)</label>
                      <div className="mb-2 flex flex-wrap gap-2">
                        {PREDEFINED_EXT_FEATURES.map(f => {
                          const isActive = (formData.extendedFeatures || '').includes(f);
                          return (
                            <span 
                              key={f}
                              onClick={() => toggleFeature('extendedFeatures', f)}
                              className={`cursor-pointer px-2 py-1 rounded text-[11px] border transition-colors ${isActive ? 'bg-primary/20 text-primary border-primary/50' : 'bg-surface-variant text-silver-text border-white/10 hover:bg-white/10'}`}
                            >
                              {f}
                            </span>
                          );
                        })}
                      </div>
                      <textarea name="extendedFeatures" value={formData.extendedFeatures || ''} onChange={handleChange} rows="5" className="w-full bg-surface-variant p-2 rounded text-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-label-sm text-on-surface-variant mb-1">What's Included (Format: Title | Subtitle | IconName)</label>
                    <textarea name="whatsIncluded" value={formData.whatsIncluded || ''} onChange={handleChange} rows="3" placeholder="EA Files (.ex4 & .ex5) | Ready for both platforms | terminal&#10;Optimized Set Files | Best settings for top pairs | settings&#10;Installation Guide | Step-by-step PDF & Video | menu_book" className="w-full bg-surface-variant p-2 rounded text-white" />
                  </div>

                  <div className="mt-4 p-4 border border-primary/30 bg-primary/5 rounded-xl">
                    <h3 className="font-bold text-primary mb-2 text-[14px]">Secure Digital Delivery (.zip)</h3>
                    <p className="text-[12px] text-on-surface-variant mb-4">
                      Upload the actual EA file here. It will be hidden securely on the server and delivered via encrypted JWT links.
                    </p>

                    {selectedProduct.file_path && !queuedFiles.eaFile && (
                      <div className="mb-4 flex items-center gap-2 text-secondary bg-secondary/10 p-3 rounded-lg border border-secondary/20">
                        <span className="material-symbols-outlined text-[20px]">check_circle</span>
                        <span className="text-[13px] font-bold truncate">Secured File Ready: {selectedProduct.file_path.split('/').pop()}</span>
                      </div>
                    )}

                    <label className="bg-primary text-on-primary px-6 py-2 rounded cursor-pointer hover:bg-primary/80 font-bold text-[14px] inline-flex items-center gap-2 max-w-full overflow-hidden" title={queuedFiles.eaFile ? queuedFiles.eaFile.name : (selectedProduct.file_path ? 'Replace EA File (.zip)' : 'Upload EA File (.zip)')}>
                      <span className="material-symbols-outlined text-[20px] shrink-0">upload_file</span>
                      <span className="truncate">{queuedFiles.eaFile ? queuedFiles.eaFile.name : (selectedProduct.file_path ? 'Replace EA File (.zip)' : 'Upload EA File (.zip)')}</span>
                      <input 
                        type="file" 
                        accept=".zip,.rar,.7z" 
                        className="hidden" 
                        onChange={(e) => handleQueueFile(e, 'eaFile')} 
                      />
                    </label>
                  </div>
                  <div className="mt-6 flex justify-between items-center border-t border-white/10 pt-4">
                    <div>
                      {selectedProduct.id !== 'new' && (
                        <button type="button" onClick={handleDelete} className="text-danger-red hover:text-red-400 font-bold px-4 py-2">
                          Delete EA
                        </button>
                      )}
                    </div>
                    <div>
                      <button type="button" onClick={() => setSelectedProduct(null)} className="mr-4 text-on-surface-variant hover:text-white">Cancel</button>
                      <button type="submit" className="bg-primary text-on-primary px-6 py-2 rounded font-bold">Save EA</button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB: CUSTOM EA REQUESTS */}
          {activeTab === 'custom_ea' && (
            <div>
              <h2 className="text-headline-sm font-bold mb-6">Custom EA Build Requests</h2>
              {customEAs.length === 0 ? <p className="text-on-surface-variant">No requests found.</p> : (
                <div className="space-y-4">
                  {customEAs.map(ea => (
                    <div key={ea.id} className="bg-surface-variant rounded-xl p-4 border border-white/5">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-lg text-primary">{ea.strategy_name}</h3>
                          <div className="text-[12px] text-on-surface-variant mt-1">
                            <span className="font-bold text-white">{ea.name || 'N/A'}</span> &bull; {ea.email || 'N/A'}
                          </div>
                        </div>
                        <span className="text-[12px] text-on-surface-variant">{new Date(ea.created_at).toLocaleString()}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-[13px] mb-4">
                        <div><span className="text-on-surface-variant">Platform:</span> {ea.platform.toUpperCase()}</div>
                        <div><span className="text-on-surface-variant">Indicators:</span> {ea.indicators}</div>
                      </div>
                      <div className="text-[13px] mb-4">
                        <span className="text-on-surface-variant block mb-1">Logic:</span>
                        <div className="bg-black/20 p-3 rounded">{ea.logic}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        {ea.news_filter === 1 && <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded">News Filter</span>}
                        {ea.trailing_stop === 1 && <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded">Trailing Stop</span>}
                        {ea.martingale === 1 && <span className="bg-red-500/20 text-red-300 px-2 py-1 rounded">Martingale</span>}
                        {ea.time_filter === 1 && <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded">Time Filter</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: EA REQUESTS (SOURCING) */}
          {activeTab === 'request_ea' && (
            <div>
              <h2 className="text-headline-sm font-bold mb-6">EA Sourcing Requests</h2>
              {eaRequests.length === 0 ? <p className="text-on-surface-variant">No requests found.</p> : (
                <table className="w-full text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="pb-3 text-on-surface-variant font-bold">Date</th>
                      <th className="pb-3 text-on-surface-variant font-bold">User</th>
                      <th className="pb-3 text-on-surface-variant font-bold">EA Name</th>
                      <th className="pb-3 text-on-surface-variant font-bold">MQL5 Link</th>
                      <th className="pb-3 text-on-surface-variant font-bold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eaRequests.map(r => (
                      <tr key={r.id} className="border-b border-white/5">
                        <td className="py-4 whitespace-nowrap text-[12px]">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-4">
                          <div className="font-bold text-white">{r.name || 'N/A'}</div>
                          <div className="text-on-surface-variant text-[11px]">{r.email || 'N/A'}</div>
                        </td>
                        <td className="py-4 font-bold text-primary">{r.ea_name}</td>
                        <td className="py-4 text-primary">
                          {r.mql5_link ? <a href={r.mql5_link} target="_blank" rel="noreferrer" className="hover:underline">View Link</a> : 'N/A'}
                        </td>
                        <td className="py-4 text-[12px] text-on-surface-variant">{r.description || 'None'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TAB: CONTACT MESSAGES */}
          {activeTab === 'contact' && (
            <div>
              <h2 className="text-headline-sm font-bold mb-6">Support Inbox</h2>
              {contactMessages.length === 0 ? <p className="text-on-surface-variant">No messages found.</p> : (
                <div className="space-y-4">
                  {contactMessages.map(msg => (
                    <div key={msg.id} className="bg-surface-variant rounded-xl p-4 border border-white/5">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-lg text-primary">{msg.subject}</h3>
                        <span className="text-[12px] text-on-surface-variant">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-[13px] text-on-surface-variant mb-4">
                        From: <span className="font-bold text-white">{msg.name}</span> ({msg.email})
                      </div>
                      <div className="text-[14px] bg-black/20 p-4 rounded whitespace-pre-wrap">
                        {msg.message}
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/5 text-right">
                        <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`} className="text-[12px] font-bold text-primary hover:underline">
                          Reply via Email
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: NEWSLETTER SUBS */}
          {activeTab === 'newsletter' && (
            <div>
              <h2 className="text-headline-sm font-bold mb-6">Newsletter Subscribers</h2>
              {newsletterSubs.length === 0 ? <p className="text-on-surface-variant">No subscribers found.</p> : (
                <div className="bg-surface-variant rounded-xl p-4 border border-white/5">
                  <div className="mb-4 text-[13px] text-on-surface-variant">Total Subscribers: <strong>{newsletterSubs.length}</strong></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {newsletterSubs.map(sub => (
                      <div key={sub.id} className="bg-black/20 px-3 py-2 rounded text-[13px]">
                        {sub.email}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: PROMOS */}
          {activeTab === 'promos' && (
            <div>
              <h2 className="text-headline-sm font-bold mb-6">Promo Codes</h2>
              
              <form onSubmit={handleCreatePromo} className="bg-surface-variant p-6 rounded-xl border border-white/5 mb-8 flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-[12px] font-bold text-on-surface-variant mb-1">Code</label>
                  <input type="text" value={newPromo.code} onChange={e => setNewPromo({...newPromo, code: e.target.value})} className="w-full bg-black/20 border border-white/10 p-2 rounded text-white focus:border-primary focus:outline-none" placeholder="SUMMER20" required/>
                </div>
                <div className="w-[100px]">
                  <label className="block text-[12px] font-bold text-on-surface-variant mb-1">Discount %</label>
                  <input type="number" value={newPromo.discount_percent} onChange={e => setNewPromo({...newPromo, discount_percent: e.target.value})} className="w-full bg-black/20 border border-white/10 p-2 rounded text-white focus:border-primary focus:outline-none" required/>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-[12px] font-bold text-on-surface-variant mb-1">Affiliate ID (Optional)</label>
                  <input type="text" value={newPromo.affiliate_id} onChange={e => setNewPromo({...newPromo, affiliate_id: e.target.value})} className="w-full bg-black/20 border border-white/10 p-2 rounded text-white focus:border-primary focus:outline-none" placeholder="ID (e.g. 1)"/>
                </div>
                <button type="submit" className="bg-primary text-[#131418] px-6 py-2.5 rounded font-bold hover:bg-primary-hover whitespace-nowrap">Create Code</button>
              </form>
              
              <h3 className="font-bold mb-4">Admin Created Codes</h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-2 text-[12px] text-on-surface-variant">CODE</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">DISCOUNT</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">AFFILIATE</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">USES</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">SALES VOL.</th>
                      <th className="py-2 text-[12px] text-on-surface-variant text-right">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(promos || []).map(p => (
                      <tr key={p.id} className="border-b border-white/5">
                        <td className="py-4 font-bold text-white">{p.code}</td>
                        <td className="py-4 text-primary">{p.discount_percent}%</td>
                        <td className="py-4 text-[13px]">{p.affiliate_username ? p.affiliate_username : 'None'}</td>
                        <td className="py-4">{p.uses}</td>
                        <td className="py-4 text-green-400">${parseFloat(p.total_sales_volume||0).toFixed(2)}</td>
                        <td className="py-4 text-right">
                          <button onClick={() => handleDeletePromo(p.id)} className="text-red-400 text-[12px] hover:underline">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="font-bold mb-4">Affiliate Referral Codes (Auto-generated)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-2 text-[12px] text-on-surface-variant">CODE</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">DISCOUNT</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">USES</th>
                      <th className="py-2 text-[12px] text-on-surface-variant">SALES VOL.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(userPromos || []).map(up => (
                      <tr key={up.promo_code} className="border-b border-white/5">
                        <td className="py-4 font-bold text-white">{up.promo_code}</td>
                        <td className="py-4 text-primary">10%</td>
                        <td className="py-4">{up.uses}</td>
                        <td className="py-4 text-green-400">${parseFloat(up.total_sales_volume||0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
